import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useDeckStore } from '../store/deckStore';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SAMPLE = `Weekly IT standup notes:
- Platform uptime 99.4% this week, +0.3pp vs last week
- Lead time down to 11 days (was 14)
- 18 stories shipped across Payments and Identity
- Risk: vendor API rate limits on Friday deploy — need go/no-go from CISO
- Ask: approve 2 contractors for Q4 migration
- Roadmap: Q1 stabilize, Q2 multi-region, Q3 cost optimization
Chart of weekly deploys: 8, 12, 10, 15`;

export function MagicDraftModal({ open, onClose }: Props) {
  const [notes, setNotes] = useState(SAMPLE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'replace' | 'append'>('append');
  const applyMagicDraft = useDeckStore((s) => s.applyMagicDraft);
  const slideNumber = useDeckStore((s) => s.activeSlideIndex + 1);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await applyMagicDraft(notes, mode);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="magic-draft-title"
    >
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 7 }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: 2.5,
            color: 'primary.main',
            bgcolor: 'chrome.tint',
          }}
        >
          <AutoAwesomeOutlinedIcon fontSize="small" />
        </Box>
        <Box>
          <Typography id="magic-draft-title" variant="h6" component="h2">
            Draft a deck with AI
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Paste notes and they'll be mapped to your master layouts.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={busy}
          sx={{ position: 'absolute', right: 16, top: 16 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={0.75}>
          <Typography component="label" htmlFor="magic-draft-notes" variant="body2" sx={{ fontWeight: 500, color: 'neutral.main' }}>
            Source notes
          </Typography>
          <TextField
            id="magic-draft-notes"
            multiline
            fullWidth
            minRows={10}
            maxRows={18}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            helperText="Groq · Qwen3.8 27B"
            slotProps={{ input: { sx: { lineHeight: 1.5 } } }}
          />
        </Stack>
        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value as 'replace' | 'append')}
          aria-label="Where to put the drafted slides"
          sx={{ mt: 2, gap: 0.5 }}
        >
          <FormControlLabel
            value="append"
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">
                Add slides after slide {slideNumber}
                <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                  Keeps your current deck; cover and closing slides from the draft are skipped
                </Typography>
              </Typography>
            }
          />
          <FormControlLabel
            value="replace"
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">
                Replace the whole deck
                <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                  Your current slides are removed (you can undo)
                </Typography>
              </Typography>
            }
          />
        </RadioGroup>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !notes.trim()}
          onClick={() => void run()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {busy ? 'Generating…' : mode === 'append' ? 'Generate slides' : 'Generate deck'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
